/**
 * KD-3532 — comparing the room a booking currently holds with the one picked in
 * the "change room" modal.
 *
 * The two ids never arrive in the same shape. An availability search emits the
 * plain hotel room composition id, sometimes behind a per-search token
 * (`<token>##<realId>`), while a booking read back from the API carries the
 * Koob-encoded form (base64 of `HotelRoomComposition/<realId>`). Comparing them
 * as-is always says "different", which is how re-picking the very same room got
 * through to the API — where it fails, because a room the hotel keeper replaced
 * with another proposal is no longer part of a fresh availability.
 *
 * Normalizing to the numeric id is what makes the two comparable. Mirrors
 * getRawBedChoiceId() in api/services/BookingService.ts (token) and IdService
 * (encoding).
 */

const NUMERIC = /^\d+$/;

export const rawBedCompositionId = (id) => {
  if (id === undefined || id === null) {
    return undefined;
  }

  const parts = String(id).split('##');
  const withoutToken = parts.length > 1 ? parts.slice(1).join('##') : parts[0];

  if (!withoutToken) {
    return undefined;
  }
  if (NUMERIC.test(withoutToken)) {
    return withoutToken;
  }

  // Decode before falling back to parseInt: a base64 payload can perfectly well
  // start with digits, and reading those as the id would match unrelated rooms.
  try {
    const decoded = atob(withoutToken.replace('\n', ''));
    const numericTail = decoded.substring(decoded.lastIndexOf('/') + 1);
    return NUMERIC.test(numericTail) ? numericTail : withoutToken;
  } catch {
    return withoutToken;
  }
};

// `room` is a booking room composition entry: { name, bedChoice: { id, ... } }.
export const isSameBedComposition = (room, otherRoom) => {
  const id = rawBedCompositionId(room?.bedChoice?.id);
  const otherId = rawBedCompositionId(otherRoom?.bedChoice?.id);

  return Boolean(id) && id === otherId;
};
